import { API } from "@swivl-mono/api-helper";
import { Channel, EscalationStatus, IsSessionTakenOver, LocationVarKey, SessionInterface, UserInterface } from "@swivl-mono/types";
import { Button, ButtonColor } from "@swivl-mono/ui";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useMemo } from "react";
import { ImPhone } from "react-icons/im";

import { Tooltip } from "../../../../../../Components/Tooltip/Tooltip";
import { useLocationVariables } from "../../../../../../Hooks/useLocationVariables";
import CallModel, { CallBarPosition } from "../../../../../../Models/CallModel/Call.Model";
import { Member } from "../../../../../../Models/Member/Member.Current";
import MembersModel from "../../../../../../Models/Member/Members.Model";
import { UsersModel } from "../../../../../../Models/Users/Users.Model";

export function ChatFooterVoice(props: { centerX: number; user: UserInterface; session?: SessionInterface }) {
  const { centerX, user, session } = props;
  const orgVars = useLocationVariables([LocationVarKey.swivlHasVoice]);
  const swivlHasVoice = Boolean(Number(orgVars?.swivlHasVoice));
  const voiceTooltipContent = !swivlHasVoice
    ? "Voice isn't enabled for your account. Reach out to support@tryswivl.com to turn it on."
    : !MembersModel.state.isAvailable
      ? "You need to go online in order to make a call"
      : undefined;
  const { callBarPosition, escalationId } = CallModel.useState((state) => state);
  const isCallBarOpen = callBarPosition !== CallBarPosition.Hidden && callBarPosition === CallBarPosition.BottomCenter ? true : false;

  const handleCallButton = async (options: { isTakeover: boolean }) => {
    if (user.orgId && user.phoneNumber && Member.id && MembersModel.state.isAvailable) {
      const session = await API.Escalations.startOutboundVoiceEscalation(user.orgId, user.id, Member.id as number);
      if (session && session.escalation?.assignedMemberId === Member.id) {
        console.log("Got session", session);
        // Now we can send out an outbound call
        CallModel.startOutbound(session.escalation, options);
      } else {
        console.log("Could not start outbound call, ending takeover");
        const sessionEscalationId = session.escalationId || session.escalation?.id;
        if (sessionEscalationId) {
          UsersModel.endTakeoverOfSession(session.id, sessionEscalationId);
        }
      }
    }
  };

  const handleListenIn = async () => {
    if (!session || !user.orgId || !Member.id || !MembersModel.state.isAvailable) {
      return;
    }

    const sessionEscalationId = session.escalationId || session.escalation?.id;
    if (!sessionEscalationId || !session.escalation) {
      alert("No escalation found for this session.");
      return;
    }

    if (!Member.twilioToken) {
      alert("No Twilio token available. Please contact support.");
      return;
    }

    try {
      await CallModel.listenInToCall(session, session.escalation);
    } catch (error) {
      console.error("Error listening in to call:", error);
      alert(error instanceof Error ? error.message : "Failed to listen in to call. Please try again.");
    }
  };

  let buttons: any = null;

  let isCallStale = false;
  if (session?.channel === Channel.VOICE && session?.updatedAt) {
    const sessionDate = new Date(session.updatedAt);
    const cutoffDate = new Date(Date.now() - 1000 * 60); // 1 minute ago
    isCallStale = sessionDate < cutoffDate;
    console.log("[ChatFooterVoice] Call Stale Check:", {
      currentTime: new Date(),
      sessionUpdatedAt: session.updatedAt,
      sessionDate: sessionDate,
      cutoffDate: cutoffDate,
      isCallStale: isCallStale
    });
  } else {
    console.log("[ChatFooterVoice] Call Stale State Not Checked", {
      channel: session?.channel,
      updatedAt: session?.updatedAt
    });
  }

  if (session && IsSessionTakenOver(session, Member.id)) {
    if (session.escalation?.assignedMemberId === Member.id) {
      if (escalationId && escalationId === session.escalationId) {
        return null;
      } else {
        console.log("Handing state show end call button");
        buttons = (
          <Button
            color={ButtonColor.YELLOW}
            onClick={() => {
              const sessionEscalationId = session.escalationId || session.escalation?.id;
              if (sessionEscalationId) {
                UsersModel.endTakeoverOfSession(session.id, sessionEscalationId);
              }
            }}
          >
            End Takeover
          </Button>
        );
      }

      console.log("Call in progress or ringing, not showing call button");
    } else {
      console.log("Handing state show end call button");
      buttons = (
        <Button
          color={ButtonColor.YELLOW}
          onClick={() => {
            const sessionEscalationId = session.escalationId || session.escalation?.id;
            if (sessionEscalationId) {
              UsersModel.endTakeoverOfSession(session.id, sessionEscalationId);
            }
          }}
        >
          End Takeover
        </Button>
      );
    }
    console.log("Call in progress or ringing, not showing call button");
  } else if (
    session &&
    session.escalation?.status === EscalationStatus.ACTIVE &&
    session.escalation?.assignedMemberId !== Member.id &&
    session.callSid &&
    session.callStatus !== "completed"
  ) {
    // Session has active escalation assigned to another agent - show Listen in button
    const canListenIn = !isCallStale && MembersModel.state.isAvailable;

    if (canListenIn) {
      buttons = (
        <Button color={ButtonColor.YELLOW} onClick={handleListenIn} disabled={!MembersModel.state.isAvailable || isCallStale}>
          Listen in
        </Button>
      );
    } else {
      console.log("Cannot listen in - call stale or member unavailable");
      return null;
    }
  } else if (session && !IsSessionTakenOver(session, Member.id) && session.callSid && session.open && session.callStatus !== "completed") {
    buttons = (
      <Button color={ButtonColor.YELLOW} onClick={() => handleCallButton({ isTakeover: true })} disabled={isCallStale}>
        <ImPhone /> Takeover Call
      </Button>
    );
  } else {
    const callButton = (
      <Button
        color={ButtonColor.YELLOW}
        onClick={() => handleCallButton({ isTakeover: false })}
        disabled={!MembersModel.state.isAvailable || !swivlHasVoice}
      >
        <ImPhone /> Call {user?.firstName || "User"}
      </Button>
    );

    buttons = voiceTooltipContent ? (
      <Tooltip classNames={{ tooltip: "text-xs" }} content={voiceTooltipContent} placement="top">
        {callButton}
      </Tooltip>
    ) : (
      callButton
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="fixed bottom-4 flex h-[64px] w-[calc(33.33%-32px)] items-center justify-between rounded-md bg-white p-4 shadow-lg"
        key={callBarPosition}
        initial={{ opacity: 0, translateY: 80 }}
        animate={{ opacity: 1, translateY: isCallBarOpen ? 80 : 0 }}
        exit={{ opacity: 0, translateY: 80 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ right: centerX }}
      >
        <span className="text-sm font-semibold">Voice Call</span>
        {buttons}
      </motion.div>
    </AnimatePresence>
  );
}
